import React from 'react'; // eslint-disable-line no-unused-vars
import { Box, Typography } from '@mui/material';
import { convertToCurrency } from '@utilities/tools';

export interface ImpactBarProps {
  budget: number;
  defundedAmount: number;
}

const fmtCompactUSD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 0,
});

export const ImpactBar = ({ budget, defundedAmount }: ImpactBarProps): React.JSX.Element | null => {
  if (!budget || budget <= 0 || !defundedAmount || defundedAmount <= 0 || defundedAmount > budget) {
    return null;
  }

  const percentCut = (defundedAmount / budget) * 100;
  const remainingPercent = 100 - percentCut;

  const percentCutText =
    percentCut >= 1 ? `${Math.round(percentCut)}%` : `${percentCut.toFixed(1)}%`;

  const LARGE_TOTAL_THRESHOLD = 1_000_000;
  const useCompact = defundedAmount >= LARGE_TOTAL_THRESHOLD;

  const compactOrFull = (value: number): string =>
    useCompact ? fmtCompactUSD.format(value) : convertToCurrency(value);

  const label = `${percentCutText} Needed – ${compactOrFull(defundedAmount)}`;

  return (
    <Box
      role="img"
      aria-label={`${percentCutText} of the budget is needed, totaling ${compactOrFull(
        defundedAmount,
      )}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minWidth: 0,
          height: 22,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${remainingPercent}%`,
            bgcolor: '#2CAEC9',
          }}
        />

        <Box
          sx={{
            width: `${percentCut}%`,
            bgcolor: '#E65222',
          }}
        />
      </Box>

      <Typography
        component="span"
        sx={{
          flexShrink: 0,
          fontSize: 14,
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};
